import {SEASONS} from '@generic/data/assumptions';
import getMarketRateElements from './shared/getMarketRateElements';
import sharedConEdStaticRateElements from './shared/sharedConEdStaticRateElements';
import {
  MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_NOT_WESTCHESTER,
  MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_WESTCHESTER,
} from './constants';
import getSC9VDERRateElement from './shared/getSC9VDERRateElement';

// Service Classification ("SC") 9
// Rate 2
const RATE_NAME = 'sc9Rate2';

/**
 * MSC - Market Supply Charge
 * Calculated using ConEd's Market Supply Charge Calculator here:
 * https://www.coned.com/en/accounts-billing/your-bill/rate-calculators/market-supply-charge
 *
 * SC9 rate is equivalent to EL9 - General - Large Commercial Rate. Different name, same thing.
 * Rates I, III and IV are the same.
 * Rates II and V are the same.
 *
 * for EL9 I-V, for the dates Jan 1, 2024 - Dec 31, 2024 are shown as on peak/off peak/standard.
 * April 10, 2025:
 * 1. Do NOT use high/low tension. No impact on rates.
 * 2. Use the standard charge.
 * https://twist.com/a/9369/ch/691652/t/6861262/c/94867920
 */
const MSC_ENERGY_CHARGES = {
  H: 0.065776,
  I: 0.066232,
  J: 0.068005,
};

const FIXED_MONTHLY_CUSTOMER_CHARGE_DOLLARS_2025 = 500.0;

// Page 660 - ./docs/coned-tariff-rate-book-2025.pdf
// PDF from - https://lite.coned.com/_external/cerates/documents/elecPSC10/electric-tariff.pdf
const DEMAND_DELIVERY_SUMMER_MONDAY_FRIDAY_8AM_6PM_DOLLARS_PER_KW_2025 = 11.59;
const DEMAND_DELIVERY_SUMMER_MONDAY_FRIDAY_8AM_10PM_DOLLARS_PER_KW_2025 = 23.35;
const DEMAND_DELIVERY_LOW_TENSION_SUMMER_ALL_HOURS_ALL_DAYS_DOLLARS_PER_KW_2025 = 21.09;
const DEMAND_DELIVERY_OTHER_MONDAY_FRIDAY_8AM_10PM_DOLLARS_PER_KW_2025 = 18.6;
const DEMAND_DELIVERY_LOW_TENSION_OTHER_ALL_HOURS_ALL_DAYS_DOLLARS_PER_KW_2025 = 5.6;

// Page 660 - ./docs/coned-tariff-rate-book-2025.pdf
// PDF from - https://lite.coned.com/_external/cerates/documents/elecPSC10/electric-tariff.pdf
const ENERGY_DELIVERY_ALL_MONTHS_ALL_HOURS_ALL_DAYS_DOLLARS_PER_KWH_2025 = 0.0079;

// Demand
// Rates taken from PDF located at ./docs/2025-MSC-Capacity-Charge-SC9-Rates-(Demand).pdf
// PDF saved from - https://lite.coned.com/_external/cerates/documents/elecPSC10/StatMSCCAP-120.pdf
const MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_WESTCHESTER = 3.18;
const MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_NOT_WESTCHESTER = 9.99;
const getMscCapacityCharge = ({isOnMarketBasedRate, isLocatedInWestchester}) => {
  if (isOnMarketBasedRate && isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_WESTCHESTER;
  }
  if (isOnMarketBasedRate && !isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_RIDER_M_MARKET_RATE_NOT_WESTCHESTER;
  }
  if (!isOnMarketBasedRate && isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_WESTCHESTER;
  }
  if (!isOnMarketBasedRate && !isLocatedInWestchester) {
    return MSC_CAPACITY_CHARGE_NOT_MARKET_RATE_NOT_WESTCHESTER;
  }
  return 0;
};

const sc9Rate2 = ({zone, isHighTension, isOnMarketBasedRate, isLocatedInWestchester}) => ({
  name: RATE_NAME,
  title: 'SC 9 Rate II',
  minKw: 1500,
  rateElements: [
    ...sharedConEdStaticRateElements,
    ...getMarketRateElements(zone, isOnMarketBasedRate),
    ...getSC9VDERRateElement({isHighTension, isLocatedInWestchester, isOnMarketBasedRate})
      .NonStandby,
    {
      rateElementType: 'MonthlyDemand',
      name: 'MSC Capacity Charge',
      billingCategory: 'supply',
      rateComponents: [
        // Rates taken from PDF located at ./docs/2025-MSC-Capacity-Charge-SC9-Rates-(Demand).pdf
        // PDF saved from - https://lite.coned.com/_external/cerates/documents/elecPSC10/StatMSCCAP-120.pdf
        {
          charge: 0,
          months: SEASONS.map((season, i) => (season === 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5, 6, 7], // 6pm to 8am
          name: 'MSC Capacity Charge Summer - 6pm to 8am',
        },
        {
          charge: getMscCapacityCharge({isOnMarketBasedRate, isLocatedInWestchester}),
          months: SEASONS.map((season, i) => (season === 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17], // 8am to 6pm
          name: 'MSC Capacity Charge Summer - 8am to 6pm',
        },
        {
          charge: 0,
          months: SEASONS.map((season, i) => (season !== 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [22, 23, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9], // 8am to 10pm
          name: 'MSC Capacity Charge Winter - 10pm to 8am',
        },
        {
          charge: getMscCapacityCharge({isOnMarketBasedRate, isLocatedInWestchester}),
          months: SEASONS.map((season, i) => (season !== 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21], // 8am to 10pm
          name: 'MSC Capacity Charge Winter - 8am to 10pm',
        },
      ],
    },
    {
      rateElementType: 'MonthlyEnergy',
      name: 'MSC Energy Charge',
      billingCategory: 'supply',
      rateComponents: [
        {
          charge: isOnMarketBasedRate ? 0 : MSC_ENERGY_CHARGES[zone],
          name: 'MSC Energy Charge',
        },
      ],
    },
    {
      rateElementType: 'FixedPerMonth',
      name: 'Customer Charge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: FIXED_MONTHLY_CUSTOMER_CHARGE_DOLLARS_2025,
          name: 'First 5kW (min)',
        },
      ],
    },
    {
      rateElementType: 'MonthlyEnergy',
      name: 'Energy Delivery Charge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: ENERGY_DELIVERY_ALL_MONTHS_ALL_HOURS_ALL_DAYS_DOLLARS_PER_KWH_2025,
          name: 'Energy Delivery Charge',
        },
      ],
    },
    // https://lite.coned.com/_external/cerates/elec_dlm.asp
    // ./docs/april-2025-rate-update/june-2024-dynamic-load-management-surcharge.pdf
    {
      rateElementType: 'MonthlyDemand',
      name: 'Dynamic Load Management ("DLM") Surcharge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: 0.29,
          name: 'CSRP & Term-/Auto-DLM Component',
        },
        {
          charge: 0.17,
          name: 'DLC & DLRP Component',
        },
      ],
      hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21], // 8am to 10pm
    },
    {
      rateElementType: 'DemandTimeOfUse',
      name: 'Demand Delivery Charge',
      billingCategory: 'delivery',
      rateComponents: [
        // ConEd does not describe all hours for demands, which may trigger warnings in console
        {
          charge: DEMAND_DELIVERY_SUMMER_MONDAY_FRIDAY_8AM_6PM_DOLLARS_PER_KW_2025,
          months: SEASONS.map((season, i) => (season === 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17], // 8am to 6pm
          name: 'Summer - 8am to 6pm',
        },
        {
          charge: DEMAND_DELIVERY_SUMMER_MONDAY_FRIDAY_8AM_10PM_DOLLARS_PER_KW_2025,
          months: SEASONS.map((season, i) => (season === 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21], // 8am to 10pm
          name: 'Summer - 8am to 10pm',
        },
        {
          charge: DEMAND_DELIVERY_OTHER_MONDAY_FRIDAY_8AM_10PM_DOLLARS_PER_KW_2025,
          months: SEASONS.map((season, i) => (season !== 'SUMMER' ? i : -1)).filter((x) => x > -1),
          hourStarts: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21], // 8am to 10pm
          name: 'Not Summer - 8am to 10pm',
        },
        {
          charge: isHighTension
            ? 0
            : SEASONS.map((season) =>
                season === 'SUMMER'
                  ? DEMAND_DELIVERY_LOW_TENSION_SUMMER_ALL_HOURS_ALL_DAYS_DOLLARS_PER_KW_2025
                  : DEMAND_DELIVERY_LOW_TENSION_OTHER_ALL_HOURS_ALL_DAYS_DOLLARS_PER_KW_2025
              ), // yes, this is only a Low Tension charge
          name: 'All Hours',
        },
      ],
    },
    // https://lite.coned.com/_external/cerates/elec_sdr.asp
    // ./docs/april-2025-rate-update/march-2025-delivery-revenue-surcharge.pdf
    {
      id: 'deliveryRevenueSurcharge',
      rateElementType: 'MonthlyEnergy',
      name: 'Delivery Revenue Surcharge',
      billingCategory: 'delivery',
      rateComponents: [
        {
          charge: 0.0001,
          name: 'Delivery Revenue Surcharge',
        },
      ],
    },
    // ./docs/april-2025-rate-update/msc-adjustment-factor.pdf
    // PDF from - https://lite.coned.com/_external/cerates/PSC10-msc-adjustments.asp
    // Non-residential Use
    {
      id: 'mscAdjustmentFactor',
      rateElementType: 'MonthlyEnergy',
      name: 'MSC Adjustment Factor',
      billingCategory: 'supply',
      rateComponents: [
        {
          charge: isLocatedInWestchester ? 0.003734 : -0.004456,
          name: 'MSC Adjustment Factor',
        },
      ],
    },
  ],
});

export default sc9Rate2;
